import * as React from "react";
import { useQueries } from "@tanstack/react-query";
import { useSession } from "@authe/ui-core/session";
import { usePermissions } from "@authe/ui-core/permissions";
import { useOrgMembers } from "@/features/team/useOrgMembers";
import { RESOURCES } from "@/config";
import { useTeamsApi } from "@/api/adapters";
import { useMyTeamsRead } from "./useMyTeams";
import { useActiveTeam } from "./activeTeam";

/**
 * A display-only union of the rosters this caller may read — person id to name.
 *
 * What `DashboardActorNames` provides to every byline, as a value: a surface
 * that needs names in a sentence (a tooltip's "Shared by Wren Writer") cannot
 * read them one hook call per id. The queries share their keys with the
 * provider's, so a second caller costs no request. Ids it does not hold are
 * absent — print a stand-in, never the id.
 */
export function useActorDirectory(): ReadonlyMap<string, string> {
  const { session } = useSession();
  const api = useTeamsApi();
  const { can } = usePermissions();
  const canListOrgMembers = can(RESOURCES.team, "list");
  const org = useOrgMembers({ enabled: canListOrgMembers });
  const { teams } = useMyTeamsRead();
  const { team, isLoading: teamsLoading } = useActiveTeam();
  // The active team only once the list is known: until then it is the
  // primary-team alias, a read for a default team the caller may not use.
  const ids = [...new Set([...(teamsLoading ? [] : [team.id]), ...teams.map((item) => item.id)])].sort();
  const rosters = useQueries({ queries: ids.map((id) => ({
    // Same key as useTeamMembersRead: one read per team, never per byline.
    queryKey: ["collaboration-roster", session?.tenant.customerId, session?.tenant.orgId,
      session?.tenant.tenantId, session?.user.id, id],
    queryFn: () => api.listMembers({ ...team, id }),
    enabled: Boolean(session?.user.id),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })) });
  const names = new Map<string, string>();
  for (const roster of rosters) for (const member of roster.data ?? []) {
    const name = member.name?.trim();
    const label = name && name !== member.id ? name : member.email;
    if (label) names.set(member.id, label);
  }
  // Tenant-shared authors need not belong to the active team. Reuse the
  // authorized org-directory query where permitted; no admin/Auth0 lookup.
  if (canListOrgMembers) for (const member of org.members) {
    const label = member.name?.trim() || member.email;
    if (label && label !== member.id) names.set(member.id, label);
  }
  // Stable while the names are: a fresh Map every render would re-run every
  // memo that reads it.
  const key = JSON.stringify([...names]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => names, [key]);
}
